import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import StackedBarChartRoundedIcon from '@mui/icons-material/StackedBarChartRounded';
import { Box, Typography } from '@mui/material';
import React, { forwardRef, memo, useEffect, useMemo, useRef, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Label, LabelList, Legend, Tooltip, XAxis, YAxis } from 'recharts';
import { debounce } from 'throttle-debounce';
import { usePrerequisites } from '../../../contexts/Prerequisites';
import util from '../../../util';
import { getColors } from './colors';


const CustomizedLabel = ({ x, y, fill, value, ...rest }) => (
	<text
		x={x}
		y={y}
		dx={7}
		dy={-7}
		textLength={value?.length}
		fontSize="11"
		fill={fill}
		textAnchor="middle"
		dominantBaseline="middle"
		{...rest}
	>
		{value}
	</text>
);
const StackedBarChart = forwardRef(
	(
		{
			data,
			hideLegend,
			label,
			labelProps,
			legendProps,
			height = 500,
			barSize = 16,
			showTooltip = true,
			xAxisLabel,
			yAxisLabel,
			...rest
		},
		ref
	) => {
		const wrapperRef = useRef();
		const [{ strings }] = usePrerequisites();
		const renderLegend = (props) => {
			const { payload } = props;
			return (
				<Box gap={1} display="flex" flexWrap={'wrap'} justifyContent={'center'}>
					{payload.map((entry, index) => (
						<Box display="flex" alignItems={'center'} color={entry.color} gap={0.5} key={`legend-item-${index}`}>
							<BarChartRoundedIcon fontSize="inherit" />
							<Typography variant="body2" color="textPrimary" key={`item-${index}`}>
								{entry.value}
							</Typography>
						</Box>
					))}
				</Box>
			);
		};

		const tickFormatter = (value, index) => {
			const limit = 10;
			if (value?.length < limit) return value;

			return `${value?.substring?.(0, limit)}...`;
		};

		const { bars, dataset, categories, colors } = useMemo(() => {
			let cData = data || { categories: [] };
			let categories = Array.isArray(data?.categories) ? data?.categories[0]?.category || [] : [];
			let chartBars = [];
			const colors = getColors(data?.dataset?.length || 0);
			Array.isArray(data?.categories) && data.categories[0]?.category
				?.sort((category1, category2) => util.sortObjects(category1, category2, "label"));
			cData = data.dataset
				?.reduce((acc, curr, i, arr) => {
					if (categories[i]?.label) {
						let indexEntry = {
							label: categories[i]?.label || '',
							total: 0,
							series: [],
							stackId: i
						};
						for (let j = 0; j < arr.length; j++) {
							const item = arr[j];
							if (
								!chartBars.some((entry) => entry.seriesname === item.seriesname && Boolean(item?.seriesname))
							) {
								chartBars.push({
									seriesname: item.seriesname,
									color: colors[j],
									stackId: indexEntry.stackId
								});
							}
							const values = item.data.filter((val => val.category === categories[i]?.label));
							const totalSum = values.reduce((accumulator, currentValue) => accumulator + (currentValue?.value || 0), 0);
							indexEntry.series = indexEntry.series.concat([item.seriesname])
							indexEntry[item.seriesname] = totalSum;
							indexEntry.total += totalSum;
						}
						acc.push(indexEntry);
					}
					return acc;
				}, []);
			return { bars: chartBars, dataset: cData, categories, colors };
		}, [data]);

		const barsTotalWidth = barSize * dataset?.length || 0;
		const totalWidth = 300 + barsTotalWidth;
		const [actualWidth, setActualWidth] = useState(totalWidth);

		useEffect(() => {
			const wrapperWidth = wrapperRef.current?.clientWidth || 0;
			setActualWidth(Math.max(totalWidth, wrapperWidth));
			const applyEffect = debounce(350, () => {
				const wrapperWidth = wrapperRef.current?.clientWidth || 0;
				setActualWidth(Math.max(totalWidth, wrapperWidth));
			});

			window.addEventListener('beforeprint', applyEffect);
			window.addEventListener('resize', applyEffect);
			return () => {
				window.removeEventListener('resize', applyEffect);
				window.removeEventListener('beforeprint', applyEffect);
			}
		}, [totalWidth]);

		return dataset?.length > 0 ? (
			<Box
				sx={{
					overflowY: 'hidden',
					overflowX: 'auto',
					'& .recharts-wrapper': {
						margin: 'auto'
					}
				}}
				width={'100%'}
				ref={wrapperRef}
			>
				<BarChart
					width={actualWidth}
					height={height}
					data={dataset}
					margin={{ top: 30, right: 10, left: 10, bottom: 30 }}
					barSize={barSize}
					ref={ref}
				>
					<CartesianGrid strokeDasharray="10 10" />
					<XAxis
						interval={0}
						angle={270}
						dy={40}
						dx={-5}
						tick={{ fontSize: 11, textAnchor: 'middle' }}
						dataKey="label"
						width={150}
						tickFormatter={tickFormatter}
						height={130}
						label={<Label position="center" dy={50} fontSize={`11px`} fontWeight="bold" value={xAxisLabel} />}
					/>
					<YAxis
						label={
							<Label
								position="center"
								angle={270}
								dx={-30}
								fontSize={`13px`}
								fontWeight="bold"
								value={yAxisLabel}
								{...labelProps}
							/>
						}
					/>
					<CartesianGrid strokeDasharray="10 10" />
					<Tooltip cursor shared={false} />

					{dataset[0].series.map((seriesname, j) => (
						<Bar
							dataKey={seriesname}
							stackId={'1'}
							stroke={colors[j]}
							isAnimationActive={false}
							fill={colors[j]}
							key={`stack-${seriesname}-${j}`}
						>
							<LabelList
								position="top"
								content={CustomizedLabel}
								valueAccessor={(entry) => {
									if (entry?.tooltipPayload?.[0]?.dataKey === entry?.series?.[entry?.series?.length - 1]) {
										return entry.total;
									}
									return '';
								}}
							/>
						</Bar>
					))}

					{!hideLegend && (
						<Legend
							wrapperStyle={{ height: '30%', overflowY: 'auto' }}
							content={renderLegend}
							{...legendProps}
						/>
					)}
				</BarChart>
			</Box>
		) : (
			<Box
				height={height}
				display="flex"
				alignItems="center"
				color="text.disabled"
				flexDirection={'column'}
				justifyContent="center"
			>
				<Typography variant="h1">
					<StackedBarChartRoundedIcon fontSize="inherit" />
				</Typography>
				<Typography variant="h5">{strings.NoRecordFound || 'No Record found'}</Typography>
			</Box>
		);
	}
);

export default memo(StackedBarChart, ({ data: prevData }, { data: nextData }) => !util.deepEqual(prevData, nextData));
